import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import Header from '../../components/Header';
import Appfooter from '../../components/Appfooter';
import Load from '../../components/Load';
import CarrinhoPainel from '../../components/CarrinhoPainel';
import { useCarrinho } from '../../context/CarrinhoContext';
import {
  QUADRA_POR_ID,
  DISPONIBILIDADE_QUADRA,
  PRECOS_QUADRA,
  TIPO_PISO_LABEL,
  formatCurrency,
  todayISO,
  SlotDisponivel,
} from '../../services/quadraService';
import { buscarDescontoAlunoParaUsuario } from '../../services/ligaLocalService';

const MEU_SALDO_QUADRA = gql`
  query MeuSaldoQuadra($localId: ID!) {
    meuSaldoNoLocal(localId: $localId)
  }
`;

const DIAS_PT = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const addDays = (iso: string, n: number) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};

const QuadraDetalhe: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { adicionarItem, removerItem, itens: itensCarrinho } = useCarrinho();

  const [data, setData] = useState(searchParams.get('data') ?? todayISO());
  const [abaAtiva, setAbaAtiva] = useState<'horarios' | 'precos' | 'info'>('horarios');
  const [erroCarrinho, setErroCarrinho] = useState('');
  const [percentualDesconto, setPercentualDesconto] = useState<number | null>(null);

  const { data: quadraData, loading: loadingQuadra } = useQuery(QUADRA_POR_ID, { variables: { id } });
  const { data: precosData } = useQuery(PRECOS_QUADRA, { variables: { quadraId: id } });

  const [buscarDisponibilidade, { data: dispData, loading: loadingDisp }] = useLazyQuery(DISPONIBILIDADE_QUADRA, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (id && data) buscarDisponibilidade({ variables: { quadraId: id, data } });
  }, [id, data, buscarDisponibilidade]);

  // Busca desconto de aluno quando o local da quadra estiver disponível
  useEffect(() => {
    const localId = quadraData?.quadraPorId?.local?.id;
    if (!localId) return;
    buscarDescontoAlunoParaUsuario(localId).then(setPercentualDesconto);
  }, [quadraData?.quadraPorId?.local?.id]);

  const quadraLocalId = quadraData?.quadraPorId?.local?.id;
  const { data: saldoQuadraData } = useQuery(MEU_SALDO_QUADRA, {
    variables: { localId: quadraLocalId ?? '' },
    skip: !quadraLocalId,
    fetchPolicy: 'cache-first',
  });
  const saldoCredito: number = saldoQuadraData?.meuSaldoNoLocal ?? 0;

  const slots: SlotDisponivel[] = dispData?.disponibilidadeQuadra ?? [];
  const quadra = quadraData?.quadraPorId;
  const precos = precosData?.precosQuadra ?? [];

  // Slots desta quadra+data que estão no carrinho
  const slotsNoCarrinho = itensCarrinho.filter(
    i => i.quadraId === id && i.dataAgendamento === data
  );

  const calcularValorSlot = (horaInicio: string, horaFim: string): { valor: number | undefined; valorOriginal: number | undefined } => {
    const diasSemana = ['DOMINGO', 'SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'];
    const diaSemana = diasSemana[new Date(data + 'T12:00:00').getDay()];
    const preco = (precosData?.precosQuadra ?? []).find(
      (p: any) => p.diaSemana === diaSemana && p.horaInicio <= horaInicio && p.horaFim >= horaFim
    );
    if (!preco) return { valor: undefined, valorOriginal: undefined };
    const [hI, mI] = horaInicio.split(':').map(Number);
    const [hF, mF] = horaFim.split(':').map(Number);
    const valorBase = ((hF * 60 + mF - hI * 60 - mI) / 60) * preco.valorPorHora;
    if (percentualDesconto != null && percentualDesconto > 0) {
      const valorComDesconto = Math.round(valorBase * (1 - percentualDesconto / 100) * 100) / 100;
      return { valor: valorComDesconto, valorOriginal: valorBase };
    }
    return { valor: valorBase, valorOriginal: undefined };
  };

  const toggleSlot = (slot: SlotDisponivel) => {
    if (!slot.disponivel || !quadra) return;
    setErroCarrinho('');
    const jaNoCarrinho = slotsNoCarrinho.some(i => i.horaInicio === slot.horaInicio);
    if (jaNoCarrinho) {
      removerItem(id!, slot.horaInicio);
    } else {
      const erro = adicionarItem({
        quadraId: id!,
        quadraNome: quadra.nome,
        localId: quadra.local.id,
        localNome: quadra.local.nome,
        dataAgendamento: data,
        horaInicio: slot.horaInicio,
        horaFim: slot.horaFim,
        ...calcularValorSlot(slot.horaInicio, slot.horaFim),
      });
      if (erro) setErroCarrinho(erro);
    }
  };

  const quadrasIds = (searchParams.get('quadras') ?? '').split(',').filter(Boolean);
  const quadrasNomes = (searchParams.get('quadrasNomes') ?? '').split(',').filter(Boolean).map(decodeURIComponent);
  const quadraIdx = parseInt(searchParams.get('idx') ?? '0', 10);
  
  // Usa os dados de disponibilidade que já vêm na URL (calculados pela página anterior)
  // Não precisa re-buscar aqui - evita loop de requests
  const quadrasDispParam = (searchParams.get('quadrasDisp') ?? '').split(',').filter(Boolean).map(Number);
  const dispQuadras = quadrasDispParam.length === quadrasIds.length ? quadrasDispParam : quadrasIds.map(() => -1);

  const navegarParaQuadra = (novoIdx: number) => {
    const novoId = quadrasIds[novoIdx];
    const p = new URLSearchParams(searchParams);
    p.set('idx', String(novoIdx));
    navigate(`/quadras/${novoId}?${p.toString()}`);
  };

  const diaSemanaData = DIAS_PT[new Date(data + 'T12:00:00').getDay()];
  const imagens = quadra?.imagens?.slice().sort((a: any, b: any) => a.ordem - b.ordem).map((img: any) => img.url) ?? [];
  const [imgAtiva, setImgAtiva] = useState(0);
  useEffect(() => { setImgAtiva(0); }, [quadra?.id]);

  if (loadingQuadra) return <Fragment><Header /><div className="main-content"><Load /></div><Appfooter /></Fragment>;
  if (!quadra) return <Fragment><Header /><div className="main-content p-4"><p>Quadra não encontrada.</p></div><Appfooter /></Fragment>;

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">

            <div className="d-flex align-items-center mb-3 gap-2">
              <button className="btn btn-light btn-sm rounded-xl" onClick={() => navigate(-1)}>
                <i className="feather-arrow-left me-1"></i> Voltar
              </button>
              <span className="font-xssss text-grey-500">
                {quadra.local?.nome} / {quadra.nome}
              </span>
            </div>

            <div className="row">
              <div className="col-12">

                {/* Galeria */}
                <div className="card shadow-xss rounded-xxl border-0 overflow-hidden mb-3">
                  {imagens.length > 0 ? (
                    <div style={{ position: 'relative' }}>
                      <img src={imagens[imgAtiva]} alt={quadra.nome} style={{ width: '100%', height: 280, objectFit: 'cover', display: 'block' }} />
                      {imagens.length > 1 && (
                        <>
                          <button
                            onClick={() => setImgAtiva(i => (i - 1 + imagens.length) % imagens.length)}
                            style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: 8, color: '#fff', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                          ><i className="feather-chevron-left"></i></button>
                          <button
                            onClick={() => setImgAtiva(i => (i + 1) % imagens.length)}
                            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: 8, color: '#fff', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                          ><i className="feather-chevron-right"></i></button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ height: 200, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                      className="d-flex align-items-center justify-content-center">
                      <i className="bi bi-grid-3x3 text-white" style={{ fontSize: 64 }}></i>
                    </div>
                  )}
                  {imagens.length > 1 && (
                    <div className="d-flex gap-2 p-2">
                      {imagens.map((img: string, i: number) => (
                        <img
                          key={i}
                          src={img}
                          alt=""
                          onClick={() => setImgAtiva(i)}
                          style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', opacity: imgAtiva === i ? 1 : 0.5, outline: imgAtiva === i ? '2px solid var(--bs-primary)' : 'none' }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Info da quadra */}
                <div className="card shadow-xss rounded-xxl border-0 p-4 mb-3">
                  <div className="d-flex align-items-start justify-content-between mb-2">
                    <div>
                      <h2 className="fw-700 font-md text-grey-900 mb-1">{quadra.nome}</h2>
                      <span className="badge bg-primary-gradiant text-white font-xssss rounded-xl me-2">
                        {TIPO_PISO_LABEL[quadra.tipoPiso] ?? quadra.tipoPiso}
                      </span>
                      {!quadra.ativa && <span className="badge bg-danger font-xssss rounded-xl">Inativa</span>}
                      {percentualDesconto != null && percentualDesconto > 0 && (
                        <span className="badge bg-success text-white font-xssss rounded-xl ms-1">
                          <i className="bi bi-percent me-1"></i>{percentualDesconto}% desconto aluno
                        </span>
                      )}
                      {saldoCredito > 0 && (
                        <div className="bg-greylight rounded-xl px-2 py-1 ms-1 d-inline-flex align-items-center gap-1">
                          <i className="bi bi-wallet2 text-grey-600" style={{ fontSize: 11 }}></i>
                          <span className="font-xssss text-grey-600">Crédito</span>
                          <span className="font-xssss fw-700 text-success">{formatCurrency(saldoCredito)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {quadra.descricao && <p className="font-xsss text-grey-600 mb-2">{quadra.descricao}</p>}
                  {quadra.observacao && (
                    <div className="alert alert-warning font-xssss py-2 mb-0">
                      <i className="feather-info me-1"></i>{quadra.observacao}
                    </div>
                  )}
                </div>

                {/* Abas */}
                <div className="card shadow-xss rounded-xxl border-0 mb-3">
                  <div className="d-flex border-bottom">
                    {(['horarios', 'precos', 'info'] as const).map(aba => (
                      <button
                        key={aba}
                        className={`btn flex-1 rounded-0 font-xsss fw-600 py-3 ${abaAtiva === aba ? 'text-primary border-bottom border-primary border-2' : 'text-grey-500'}`}
                        style={{ borderBottom: abaAtiva === aba ? '2px solid var(--bs-primary)' : '2px solid transparent' }}
                        onClick={() => setAbaAtiva(aba)}
                      >
                        {aba === 'horarios' && <><i className="feather-clock me-1"></i>Horários</>}
                        {aba === 'precos' && <><i className="feather-tag me-1"></i>Preços</>}
                        {aba === 'info' && <><i className="feather-map-pin me-1"></i>Local</>}
                      </button>
                    ))}
                  </div>

                  <div className="p-4">
                    {abaAtiva === 'horarios' && (
                      <>
                        <div className="d-flex flex-wrap gap-5 align-items-start mb-4 pb-3 border-bottom">
                          <div className="d-flex flex-column align-items-center gap-1">
                            <label className="font-xssss text-grey-500 fw-600">Data</label>
                            <div className="d-flex align-items-center gap-1">
                              <button
                                type="button"
                                className="btn btn-light rounded-xl px-2"
                                style={{ height: 38 }}
                                onClick={() => { const d = addDays(data, -1); if (d >= todayISO()) setData(d); }}
                                disabled={data <= todayISO()}
                              ><i className="feather-chevron-left"></i></button>
                              <input
                                type="date"
                                className="form-control rounded-xl font-xsss"
                                value={data}
                                min={todayISO()}
                                onChange={e => setData(e.target.value)}
                                style={{ width: 145, height: 38 }}
                              />
                              <button
                                type="button"
                                className="btn btn-light rounded-xl px-2"
                                style={{ height: 38 }}
                                onClick={() => setData(addDays(data, 1))}
                              ><i className="feather-chevron-right"></i></button>
                            </div>
                            <span className="font-xssss text-grey-500">{diaSemanaData}</span>
                          </div>
                          {quadrasIds.length > 1 && (
                            <div className="d-flex flex-column align-items-center gap-1">
                              <label className="font-xssss text-grey-500 fw-600">Quadra</label>
                              <select
                                className="form-select rounded-xl font-xsss"
                                style={{ height: 38, minWidth: 160 }}
                                value={quadraIdx}
                                onChange={e => navegarParaQuadra(Number(e.target.value))}
                              >
                                {quadrasIds.map((qId, i) => (
                                  <option key={qId} value={i}>
                                    {quadrasNomes[i] ?? `Quadra ${i + 1}`}
                                    {dispQuadras[i] >= 0 ? ` (${dispQuadras[i]} livre${dispQuadras[i] !== 1 ? 's' : ''})` : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>

                        {erroCarrinho && (
                          <div className="alert alert-warning font-xssss py-2 mb-3">
                            <i className="feather-alert-triangle me-1"></i>{erroCarrinho}
                          </div>
                        )}

                        {loadingDisp && <Load />}

                        {!loadingDisp && slots.length === 0 && (
                          <p className="text-grey-500 font-xsss text-center py-3">Nenhum horário disponível para esta data.</p>
                        )}

                        <div className="d-flex flex-wrap gap-2">
                          {slots.map((slot, i) => {
                            const selecionado = slotsNoCarrinho.some(s => s.horaInicio === slot.horaInicio);
                            return (
                              <button
                                key={i}
                                onClick={() => toggleSlot(slot)}
                                disabled={!slot.disponivel}
                                className={`btn rounded-xl font-xssss fw-600 px-3 py-2 ${
                                  selecionado
                                    ? 'bg-primary-gradiant text-white'
                                    : slot.disponivel
                                    ? 'btn-outline-success'
                                    : 'btn-light text-grey-400'
                                }`}
                                style={{ minWidth: 90, opacity: slot.disponivel ? 1 : 0.5 }}
                                title={slot.disponivel ? 'Disponível' : 'Ocupado'}
                              >
                                {slot.horaInicio}
                                {!slot.disponivel && <i className="feather-lock ms-1" style={{ fontSize: 10 }}></i>}
                              </button>
                            );
                          })}
                        </div>

                        {slots.length > 0 && (
                          <div className="d-flex gap-3 mt-3">
                            <span className="d-flex align-items-center gap-1 font-xssss text-grey-500">
                              <span style={{ width: 12, height: 12, borderRadius: 3, background: '#22c55e', display: 'inline-block' }}></span> Disponível
                            </span>
                            <span className="d-flex align-items-center gap-1 font-xssss text-grey-500">
                              <span style={{ width: 12, height: 12, borderRadius: 3, background: '#e5e7eb', display: 'inline-block' }}></span> Ocupado
                            </span>
                            <span className="d-flex align-items-center gap-1 font-xssss text-grey-500">
                              <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--bs-primary)', display: 'inline-block' }}></span> Selecionado
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {abaAtiva === 'precos' && (
                      <>
                        {precos.length === 0 ? (
                          <p className="text-grey-500 font-xsss text-center py-3">Nenhuma tabela de preços cadastrada.</p>
                        ) : (() => {
                          const diasSemana = ['DOMINGO', 'SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'];
                          const diaSemanaAtual = diasSemana[new Date(data + 'T12:00:00').getDay()];
                          const precosDia = precos
                            .filter((p: any) => p.diaSemana === diaSemanaAtual)
                            .sort((a: any, b: any) => a.horaInicio.localeCompare(b.horaInicio));
                          return precosDia.length === 0 ? (
                            <p className="text-grey-500 font-xsss text-center py-3">Nenhum preço cadastrado para {diaSemanaData}.</p>
                          ) : (
                            <>
                              <p className="font-xssss text-grey-500 mb-2">Preços para <strong>{diaSemanaData}</strong></p>
                              {percentualDesconto != null && percentualDesconto > 0 && (
                                <div className="alert alert-success font-xssss py-2 mb-2">
                                  <i className="bi bi-percent me-1"></i>
                                  Você tem <strong>{percentualDesconto}% de desconto</strong> como aluno neste local.
                                </div>
                              )}
                              <div className="table-responsive">
                                <table className="table table-sm font-xsss">
                                  <thead>
                                    <tr className="text-grey-500">
                                      <th>Horário</th>
                                      <th>Valor por período</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {precosDia.map((p: any) => (
                                      <tr key={p.id}>
                                        <td>{p.horaInicio} – {p.horaFim}</td>
                                        <td className="fw-700 text-success">{formatCurrency(p.valorPorHora)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}

                    {abaAtiva === 'info' && quadra.local && (
                      <div>
                        <h5 className="fw-700 font-xss mb-2">{quadra.local.nome}</h5>
                        {quadra.local.descricao && <p className="font-xsss text-grey-600 mb-2">{quadra.local.descricao}</p>}
                        {quadra.local.endereco && (
                          <p className="font-xsss text-grey-600 mb-2">
                            <i className="feather-map-pin me-1 text-primary"></i>
                            {quadra.local.endereco.logradouro}, {quadra.local.endereco.numero}
                            {quadra.local.endereco.complemento ? ` - ${quadra.local.endereco.complemento}` : ''} —{' '}
                            {quadra.local.endereco.bairro}, {quadra.local.endereco.cidade?.nome} - {quadra.local.endereco.cidade?.uf}
                            {quadra.local.endereco.cep ? ` — CEP ${quadra.local.endereco.cep}` : ''}
                          </p>
                        )}
                        {quadra.local.urlLocalizacao && (
                          <a
                            href={quadra.local.urlLocalizacao}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-outline-primary rounded-xl font-xssss fw-600"
                          >
                            <i className="bi bi-map me-1"></i> Ver no Google Maps
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <CarrinhoPainel />
      <Appfooter />
    </Fragment>
  );
};

export default QuadraDetalhe;
